import { useEffect, useState } from "react";
import type { AgentAvatarDTO } from "../../shared/types";
import { get } from "../lib/api";
import { Avatar } from "./ui";

/**
 * Every agent's name and picture, read once per page: a card, a message or a run row knows an agent's id or its
 * slug and nothing else. One request, shared by every mention on the page.
 */
let pending: Promise<AgentAvatarDTO[]> | null = null;
const load = () => (pending ??= get<AgentAvatarDTO[]>("/api/agents/avatars").catch(() => []));

/** After an avatar is changed, so the next page that mounts reads it again. */
export const forgetAgentAvatars = () => {
  pending = null;
};

export function useAgentAvatars(): Map<string, AgentAvatarDTO> {
  const [index, setIndex] = useState<Map<string, AgentAvatarDTO>>(new Map());
  useEffect(() => {
    let live = true;
    void load().then((rows) => {
      if (!live) return;
      const map = new Map<string, AgentAvatarDTO>();
      for (const row of rows) {
        map.set(row.agentId, row);
        map.set(row.slug, row);
      }
      setIndex(map);
    });
    return () => {
      live = false;
    };
  }, []);
  return index;
}

/** An agent wherever it is mentioned: its own picture, the panda for Pandito, its initials until then. */
export function AgentAvatar({ agent, name, size = "md" }: { agent?: string | null; name: string; size?: "xs" | "sm" | "md" | "lg" }) {
  const index = useAgentAvatars();
  const found = agent ? index.get(agent) : undefined;
  return <Avatar name={found?.name ?? name} src={found?.avatarUrl} size={size} />;
}

/** Several agents as small overlapping pictures, the rest counted, each name on hover: the agents that hold a template. */
export function AgentPictures({ agents, max = 4 }: { agents: { id: string; name: string }[]; max?: number }) {
  const rest = agents.slice(max);
  return (
    <span className="flex shrink-0 items-center gap-1">
      <span className="flex -space-x-1.5">
        {agents.slice(0, max).map((a) => (
          <span key={a.id} title={a.name} className="rounded-full ring-2 ring-surface">
            <AgentAvatar agent={a.id} name={a.name} size="sm" />
          </span>
        ))}
      </span>
      {rest.length > 0 && <span className="text-xs text-muted" title={rest.map((a) => a.name).join(", ")}>+{rest.length}</span>}
    </span>
  );
}
